import { c as _c } from "react/compiler-runtime";
import { CookieOptions } from '@/sections/cookies/CookieConsent.config';
import { Dispatch, SetStateAction, useEffect, useSyncExternalStore } from 'react';
import { hostEnv } from '../config/helpers/configEnvironment';
const stringifyCookieOptions = (options: CookieOptions) => {
  const parts: string[] = ['SameSite=lax', 'Path=/'];

  // Otherwise the cookie will not work on localhost
  if (hostEnv === 'server') {
    parts.push('Secure');
  }
  parts.push(`max-age=${options.maxAge}`);
  return parts.join('; ');
};
export const createCookieStore = <Type,>({
  key,
  parse,
  stringify,
  options
}: {
  key: string;
  parse: (value: string | null) => Type | null;
  stringify: (value: Type | null) => string | null;
  options: CookieOptions;
}) => {
  const listeners = new Set<(value: Type | null) => void>();
  let itemCache: {
    value: Type | null;
  } = {
    value: null
  };
  const clearStorage = () => {
    itemCache = {
      value: null
    };
    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  };
  const getItem = () => {
    if (itemCache.value != null) {
      return itemCache.value;
    }
    for (const cookie of document.cookie.split('; ')) {
      const [cookieKey, cookieValue] = cookie.split('=');
      if (cookieKey === key) {
        const itemValue = parse(cookieValue);
        itemCache = {
          value: itemValue
        };
        return itemValue;
      }
    }
    return null;
  };
  const setItem = (value: Type | null) => {
    const stringified = stringify(value);
    itemCache = {
      value
    };
    if (value == null || stringified == null) clearStorage();else document.cookie = `${key}=${stringified}; ${stringifyCookieOptions(options)}`;
  };
  const getSnapshot = (initialValue: Type): Type => {
    const item = getItem();
    if (item != null) {
      return item;
    }
    return initialValue;
  };
  const set: Dispatch<SetStateAction<Type | null>> = (value: (Type | null) | ((oldValue: Type | null) => Type | null)) => {
    const valueToStore = value instanceof Function ? value(getItem()) : value;
    setItem(valueToStore);
    for (const listener of listeners) {
      listener(valueToStore);
    }
  };
  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };
  const renew = () => {
    const item = getItem();
    if (item == null) return;
    setItem(item);
  };
  return {
    getSnapshot,
    set,
    subscribe,
    parse,
    renew
  };
};

/**
 * Store a string enum in cookie
 */
export const createCookieStringStore = <AllowedValues extends ReadonlyArray<string>,>(key: string, allowedValues: AllowedValues, options: CookieOptions) => {
  return createCookieStore({
    key,
    parse: value => {
      if (value == null) return null;
      if (allowedValues.includes(value)) return value as AllowedValues[number];
      return null;
    },
    stringify: value => value,
    options
  });
};
export const useCookieStore = (store, initialValue, serverValue) => {
  const $ = _c(13);
  let t0;
  if ($[0] !== initialValue || $[1] !== store) {
    t0 = () => store.getSnapshot(initialValue);
    $[0] = initialValue;
    $[1] = store;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== initialValue || $[4] !== serverValue || $[5] !== store) {
    t1 = () => store.parse(serverValue) ?? initialValue;
    $[3] = initialValue;
    $[4] = serverValue;
    $[5] = store;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const value = useSyncExternalStore(store.subscribe, t0, t1);
  let t2;
  let t3;
  if ($[7] !== store) {
    t2 = () => {
      store.renew();
    };
    t3 = [store];
    $[7] = store;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  if ($[10] !== store.set || $[11] !== value) {
    t4 = [value, store.set];
    $[10] = store.set;
    $[11] = value;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4 as const;
};